import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { currencyToNumber } from '@iwoca/orion';
import { cloneDeep } from 'lodash-es';
import { v4 } from 'uuid';

import {
  APPLICANT_STEP_ID,
  BUSINESS_STEP_ID,
  CHECK_ELIGIBILITY_STEP_ID,
  GET_STARTED_STEP_ID,
} from './steps';
import {
  TGetStateResponseDE,
  TPutStateRequestBodyDE,
} from '../../lapiOverrideTypesDE';
import { IwQueryClient } from '../../store/IwQueryClient';
import {
  TGetApplicationResponse,
  TGetStateResponse,
  TPutStateRequestBody,
  TRecursiveNonNullable,
} from '../../types';
import { TCompanyDetailsDataDE } from '../CompanyDetails/CompanyDetailsDE/CompanyDetailsDE';
import { TCompanyDetailsDataUK } from '../CompanyDetails/CompanyDetailsUK/CompanyDetailsUK';
import { TGetStartedDataDE } from '../GetStarted/GetStartedDE/GetStartedDE';
import { TGetStartedDataUK } from '../GetStarted/GetStartedUK/GetStartedUK';
import { TPersonalDetailsDataDE } from '../PersonalDetails/PersonalDetailsDE/PersonalDetailsDE';
import { TPersonalDetailsDataUK } from '../PersonalDetails/PersonalDetailsUK/PersonalDetailsUK';

export function normaliseStepData(customerState: TGetStateResponse) {
  const applicant = customerState.data.application?.people?.[0];

  const getAddresses = (
    addresses?: TRecursiveNonNullable<TGetStateResponse>['data']['application']['people'][number]['residential_addresses'],
  ) => {
    const normaliseAddress = (
      address?: TRecursiveNonNullable<TGetStateResponse>['data']['application']['people'][number]['residential_addresses'][number],
    ) => ({
      houseNumber: address?.house_number || '',
      houseName: address?.house_name || '',
      streetLine1: address?.street_line_1 || '',
      streetLine2: address?.street_line_2 || '',
      town: address?.town || '',
      postcode: address?.postcode || '',
      equifaxToken: address?.equifax_token || '',
    });

    if (!addresses) {
      return [normaliseAddress()];
    }

    return addresses.map((address) => normaliseAddress(address));
  };

  return {
    [GET_STARTED_STEP_ID]: {
      email: customerState.data.application?.people?.[0]?.emails?.[0]?.email,
      marketingOptIn:
        customerState.data.application?.people?.[0].emails?.[0].marketing_opt_in
          ?.agreed,
      companyType: customerState.data.application?.company
        ?.type as GetCompanySearchResponse['data'][number]['company_type'],
      companyName:
        customerState.data.application?.company?.registered_company_name || '',
      companyNumber:
        customerState.data.application?.company?.company_number || '',
      startDateMonth:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[1],
      startDateYear:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[0],
      last12MonthsTurnover:
        customerState.data.application?.company?.last_12_months_turnover?.amount?.toLocaleString(
          'en-GB',
        ),
      isVatRegistered: normaliseVATStatus(
        customerState.data.application?.company?.vat_status?.is_vat_registered,
      ),
    },
    [BUSINESS_STEP_ID]: {
      companyType: customerState.data.application?.company
        ?.type as GetCompanySearchResponse['data'][number]['company_type'],
      companyName:
        customerState.data.application?.company?.registered_company_name || '',
      companyNumber:
        customerState.data.application?.company?.company_number || '',
      startDateMonth:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[1],
      startDateYear:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[0],
      last12MonthsTurnover:
        customerState.data.application?.company?.last_12_months_turnover?.amount?.toLocaleString(
          'en-GB',
        ),
      isVatRegistered: normaliseVATStatus(
        customerState.data.application?.company?.vat_status?.is_vat_registered,
      ),
    },
    [CHECK_ELIGIBILITY_STEP_ID]: {
      last12MonthsTurnover:
        customerState.data.application?.company?.last_12_months_turnover
          ?.amount,
    },
    [APPLICANT_STEP_ID]: {
      firstName: applicant?.first_name,
      lastName: applicant?.last_name,
      isDirector: applicant?.roles?.includes('director'),
      dobDay: applicant?.date_of_birth?.split('-')[2],
      dobMonth: applicant?.date_of_birth?.split('-')[1],
      dobYear: applicant?.date_of_birth?.split('-')[0],
      phoneNumber: applicant?.phones?.[0]?.number,
      // @ts-expect-error remove when not using non-nullable for getAddresses arg type
      residentialAddresses: getAddresses(applicant?.residential_addresses),
      privacyPolicyAgreement: applicant?.privacy_policy?.agreed,
      // Needed for director lookup
      companyName:
        customerState.data.application?.company?.registered_company_name || '',
      companyNumber:
        customerState.data.application?.company?.company_number || '',
    },
    ui: customerState.data.ui,
  };
}

export function normaliseStepDataDE(customerState: TGetStateResponseDE) {
  const applicant = customerState.data.application?.people?.[0];

  return {
    [GET_STARTED_STEP_ID]: {
      email: customerState.data.application?.people?.[0]?.emails?.[0]?.email,
      marketingOptIn:
        customerState.data.application?.people?.[0].emails?.[0].marketing_opt_in
          ?.agreed,
    },
    [BUSINESS_STEP_ID]: {
      companyName:
        customerState.data.application?.company?.registered_company_name,
      crefoId: customerState.data.application?.company?.creditreform_id,
      buildingNumber:
        customerState.data.application?.company?.registered_address
          ?.building_number,
      street:
        customerState.data.application?.company?.registered_address
          ?.street_line_1,
      postcode:
        customerState.data.application?.company?.registered_address?.postcode,
      town: customerState.data.application?.company?.registered_address?.town,
      legalForm: customerState.data.application?.company?.type,
      startDateMonth:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[1],
      startDateYear:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[0],
      last12MonthsTurnover:
        customerState.data.application?.company?.last_12_months_turnover?.amount?.toLocaleString(
          'de',
        ),
      website: customerState.data.application?.company?.website,
    },
    [APPLICANT_STEP_ID]: {
      title: applicant?.title,
      firstName: applicant?.first_name,
      lastName: applicant?.last_name,
      dobDay: applicant?.date_of_birth?.split('-')[2],
      dobMonth: applicant?.date_of_birth?.split('-')[1],
      dobYear: applicant?.date_of_birth?.split('-')[0],
      phoneNumber: applicant?.phones?.[0]?.number,
      houseNumber: applicant?.residential_addresses?.[0]?.house_number,
      streetLine1: applicant?.residential_addresses?.[0]?.street_line_1,
      streetLine2: applicant?.residential_addresses?.[0]?.street_line_2,
      town: applicant?.residential_addresses?.[0]?.town,
      postcode: applicant?.residential_addresses?.[0]?.postcode,
      privacyPolicyAgreement: applicant?.privacy_policy?.agreed,
    },
    [CHECK_ELIGIBILITY_STEP_ID]: {
      last12MonthsTurnover:
        customerState.data.application?.company?.last_12_months_turnover
          ?.amount,
      startDateMonth:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[1],
      startDateYear:
        customerState.data.application?.company?.trading_from_date?.split(
          '-',
        )[0],
    },
    ui: customerState.data.ui,
  };
}

function convertDurationToMonths(
  value: number,
  unit: TGetApplicationResponse['data']['requested_products'][number]['duration']['unit'],
): number {
  const normalizedUnit = unit.toLowerCase().trim();

  if (normalizedUnit === 'years') {
    return value * 12;
  }

  if (normalizedUnit === 'days') {
    return Math.round(value / 30.44); // Average days per month
  }

  return value;
}

export function normaliseLoanDurationToMonths(
  duration: TGetApplicationResponse['data']['requested_products'][number]['duration'],
): TGetStartedDataUK['loanDuration'] {
  const newDuration = convertDurationToMonths(duration.amount, duration.unit);

  return `${newDuration}_months` as TGetStartedDataUK['loanDuration'];
}

function normaliseVATStatus(vatStatus?: boolean): 'true' | 'false' | undefined {
  if (typeof vatStatus === 'boolean') {
    return vatStatus ? 'true' : 'false';
  }

  return undefined;
}

export function denormaliseCompanyDataUK(
  prevState: TGetStateResponse['data'],
  formData: TCompanyDetailsDataUK,
): TPutStateRequestBody {
  const data = {
    ...prevState,
    application: {
      ...prevState.application,
      company: {
        ...prevState.application?.company,
        registered_company_name: formData.companyName,
        company_number: formData.companyNumber,
        type: formData.companyType,
        trading_from_date: denormaliseDate(
          '01',
          formData.startDateMonth,
          formData.startDateYear,
        ),
        last_12_months_turnover: {
          amount: currencyToNumber(formData.last12MonthsTurnover, {
            currencySymbol: true,
            locale: 'en-GB',
            minorUnits: false,
          }),
        },
      },
    },
  };

  if (formData.isVatRegistered) {
    data.application.company.vat_status = {
      is_vat_registered: formData.isVatRegistered === 'true',
    };
  }

  return {
    data,
  };
}

export function denormaliseCompanyDataDE(
  prevState: TGetStateResponseDE['data'],
  formData: TCompanyDetailsDataDE,
): TPutStateRequestBodyDE {
  return {
    data: {
      ...prevState,
      application: {
        ...prevState.application,
        company: {
          ...prevState.application?.company,
          creditreform_id: formData.crefoId,
          registered_company_name: formData.companyName,
          type: formData.legalForm,
          registered_address: {
            building_number: formData.buildingNumber,
            street_line_1: formData.street,
            postcode: formData.postcode,
            town: formData.town,
          },
          trading_from_date: denormaliseDate(
            '01',
            formData.startDateMonth,
            formData.startDateYear,
          ),
          last_12_months_turnover: {
            amount: currencyToNumber(formData.last12MonthsTurnover, {
              currencySymbol: true,
              locale: 'de-DE',
              minorUnits: false,
            }),
          },
          website: denormaliseWebsite(formData.website),
        },
      },
    },
  };
}

export function denormalisePersonalDataUK(
  prevState: TGetStateResponse['data'],
  formData: TPersonalDetailsDataUK,
): TPutStateRequestBody {
  const dateNow = new Date();
  dateNow.setMinutes(-1 * dateNow.getTimezoneOffset());

  return {
    data: {
      ...prevState,
      application: {
        ...prevState.application,
        people: [
          {
            ...prevState.application?.people?.[0],
            uid: prevState.application?.people?.[0]?.uid || v4(),
            privacy_policy: {
              agreed: formData.privacyPolicyAgreement,
              datetime: dateNow.toISOString(),
            },
            first_name: formData.firstName,
            last_name: formData.lastName,
            date_of_birth: denormaliseDate(
              formData.dobDay,
              formData.dobMonth,
              formData.dobYear,
            ),
            residential_addresses: formData.residentialAddresses.map(
              (address) => ({
                uid: v4(),
                house_name: address.houseName || undefined,
                house_number: address.houseNumber || undefined,
                postcode: address.postcode,
                equifax_token: address.equifaxToken || undefined,
                street_line_1: address.streetLine1 || undefined,
                street_line_2: address.streetLine2 || undefined,
                town: address.town || undefined,
              }),
            ),
            phones: [
              {
                uid:
                  prevState.application?.people?.[0]?.phones?.[0]?.uid || v4(),
                number: formData.phoneNumber,
                type: 'primary',
              },
            ],
          },
        ],
      },
    },
  };
}

export function denormalisePersonalDataDE(
  prevState: TGetStateResponseDE['data'],
  formData: TPersonalDetailsDataDE,
): TPutStateRequestBodyDE {
  const dateNow = new Date();
  dateNow.setMinutes(-1 * dateNow.getTimezoneOffset());

  return {
    data: {
      ...prevState,
      application: {
        ...prevState.application,
        people: [
          {
            ...prevState.application?.people?.[0],
            uid: prevState.application?.people?.[0]?.uid || v4(),
            privacy_policy: {
              agreed: formData.privacyPolicyAgreement,
              datetime: dateNow.toISOString(),
            },
            title: formData.title,
            first_name: formData.firstName,
            last_name: formData.lastName,
            date_of_birth: denormaliseDate(
              formData.dobDay,
              formData.dobMonth,
              formData.dobYear,
            ),
            residential_addresses: [
              {
                uid: v4(),
                house_number: formData.houseNumber,
                postcode: formData.postcode,
                street_line_1: formData.streetLine1,
                street_line_2: formData.streetLine2 || undefined,
                town: formData.town,
              },
            ],
            phones: [
              {
                uid:
                  prevState.application?.people?.[0]?.phones?.[0]?.uid || v4(),
                number: formData.phoneNumber,
                type: 'primary',
              },
            ],
          },
        ],
      },
    },
  };
}

export function denormaliseGetStartedData<
  TState extends TGetStateResponse | TGetStateResponseDE,
>(prevState: TState['data'], formData: TGetStartedDataUK | TGetStartedDataDE) {
  return {
    data: {
      ...prevState,
      application: {
        ...prevState.application,
        people: [
          {
            ...prevState.application?.people?.[0],
            uid: prevState.application?.people?.[0]?.uid || v4(),
            roles: ['applicant', 'director', 'guarantor'],
            emails: [
              {
                email: formData.email,
                marketing_opt_in: {
                  agreed: formData.marketingOptIn || false,
                  datetime: new Date().toISOString(),
                },
                type: 'primary',
                uid: v4(),
              },
            ],
          },
        ],
      },
    },
  } as TState;
}

function denormaliseDate(day: string, month: string, year: string) {
  const twoDigitDay = day.trim().padStart(2, '0');
  const twoDigitMonth = month.trim().padStart(2, '0');

  return `${year.trim()}-${twoDigitMonth}-${twoDigitDay}`;
}

function denormaliseWebsite(website?: string) {
  if (!website) {
    return undefined;
  }

  const websiteWithoutWhitespace = website.replaceAll(/\s/g, '');

  const websiteIncludesHttp =
    websiteWithoutWhitespace.match(/(http)(?!s)|(https)/g);

  return websiteIncludesHttp
    ? websiteWithoutWhitespace
    : `https://${websiteWithoutWhitespace}`;
}

export function denormaliseMarketingPoll<
  TState extends TGetStateResponse | TGetStateResponseDE,
>(prevState: TState['data']): TState['data'] {
  const marketingPollAnswer = sessionStorage.getItem('marketing_poll_answer');
  const marketingPollDetail = sessionStorage.getItem('marketing_poll_detail');

  const updateState = cloneDeep(prevState);

  if (updateState.application?.people && marketingPollAnswer) {
    const person = updateState.application.people[0];

    person.how_did_you_find_us = {
      answer: marketingPollAnswer as NonNullable<
        typeof person.how_did_you_find_us
      >['answer'],
      details: marketingPollDetail || '',
    };

    return updateState;
  }

  return prevState;
}

export function getMarketingPollUpdatedState<
  TState extends TGetStateResponse | TGetStateResponseDE,
>(): TState['data'] | undefined {
  const queryStateCache: TState | undefined =
    IwQueryClient.getOrCreate().getQueryData(['fetchGetState']);

  if (!queryStateCache) return undefined;

  return denormaliseMarketingPoll<TState>(queryStateCache.data);
}
